import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import SchoolInput from '../../SchoolInput';
import PictureDaySignup from '../../PictureDaySignup';
import SignupThankYou from '../../StudentIdForm/SignupThankYou';
import shoobMark from '../../../assets/shoob-mark.png';
import styles from './PictureDayRegistration.module.scss';

// "Tell me when my child's photos are ready", reached from the nav and the home
// page rather than from the entry screen.
//
// SAME SIGNUP, SAME CARD as the entry screen. The old page here was Bootstrap 3
// panels and inline styles, collected the child's name, grade AND teacher, and
// posted to /customer_registrations -- which requires a matching Student and
// silently discards the signup when it cannot find one. That is the bug this
// whole feature exists to fix, so this page stops using it.
//
// WHAT IT ASKS FOR IS DELIBERATELY SMALLER. School, one contact and the child's
// name; the STUDENT ID is the optional field, and only it. Grade and teacher
// are gone, because neither improves the match (the roster's grade is a year
// stale in exactly the case that matters) and every extra field costs signups
// on a page whose entire purpose is to collect one.
const STRINGS = {
  en: {
    title: 'Picture Day Updates',
    subtitle: 'We’ll let you know the moment your student’s photos are ready.',
    schoolLabel: 'School',
    schoolPlaceholder: 'Type your school name',
    studentHeading: 'Your student',
    studentHint: 'The student ID is optional — add it for secure verification.',
    firstName: 'First name',
    lastName: 'Last name',
    studentId: 'Student ID (optional)',
    missingName: 'Please enter your student’s first and last name.',
    privacyTitle: 'Your Privacy Matters',
    privacyBody: 'Your photos are stored securely and only accessible with your unique code.',
  },
  es: {
    title: 'Avisos del día de fotos',
    subtitle: 'Le avisaremos en cuanto las fotos de su estudiante estén listas.',
    schoolLabel: 'Escuela',
    schoolPlaceholder: 'Escriba el nombre de su escuela',
    studentHeading: 'Su estudiante',
    studentHint: 'El número de estudiante es opcional — agréguelo para una verificación segura.',
    firstName: 'Nombre',
    lastName: 'Apellido',
    studentId: 'Número de estudiante (opcional)',
    missingName: 'Ingrese el nombre y apellido de su estudiante.',
    privacyTitle: 'Su privacidad es importante',
    privacyBody: 'Sus fotos se almacenan de forma segura y solo son accesibles con su código único.',
  },
};

const PictureDayRegistration = () => {
  const location = useLocation();
  const lang = new URLSearchParams(location.search).get('lang') === 'es' ? 'es' : 'en';
  const t = STRINGS[lang] || STRINGS.en;

  const [school, setSchool] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [studentId, setStudentId] = useState('');
  const [signedUp, setSignedUp] = useState(false);

  // NAME REQUIRED, STUDENT ID OPTIONAL. Different from the entry screen, which
  // asks for no name at all -- a parent who has come here to register knows
  // their child's name, and without it we can never do better than a
  // school-level guess. The ID is what most parents do not have to hand, so it
  // is the part that stays optional.
  const nameComplete = firstName.trim().length > 0 && lastName.trim().length > 0;

  const reset = () => {
    setFirstName('');
    setLastName('');
    setStudentId('');
    setSignedUp(false);
  };

  if (signedUp) {
    return (
      <div className={styles.page} lang={lang}>
        <div className={styles.card}>
          <SignupThankYou lang={lang} onAnother={reset} />
        </div>
        <div className={styles.privacy}>
          <div className={styles.privacyTitle}>{t.privacyTitle}</div>
          <div className={styles.privacyBody}>{t.privacyBody}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page} lang={lang}>
      <div className={styles.card}>
        <div className={styles.logoBadge} aria-hidden="true">
          <img src={shoobMark} alt="" className={styles.logoMark} />
        </div>
        <h1 className={styles.title}>{t.title}</h1>
        <p className={styles.subtitle}>{t.subtitle}</p>

        <div className={styles.field}>
          <label htmlFor="school-input">{t.schoolLabel}</label>
          <SchoolInput
            onChange={setSchool}
            hideLabel
            noDefault
            openOnTypeOnly
            placeholder={t.schoolPlaceholder}
          />
        </div>

        {/* Everything below waits on the school, because a signup with no
            school is one we can never send. */}
        {school && (
          <>
            <div className={styles.studentBlock}>
              <p className={styles.studentHeading}>{t.studentHeading}</p>
              <p className={styles.studentHint}>{t.studentHint}</p>

              <div className={styles.nameRow}>
                <div className={styles.field}>
                  <label htmlFor="reg-first">{t.firstName}</label>
                  <input
                    id="reg-first"
                    type="text"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="reg-last">{t.lastName}</label>
                  <input
                    id="reg-last"
                    type="text"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="reg-student-id">{t.studentId}</label>
                <input
                  id="reg-student-id"
                  type="text"
                  value={studentId}
                  onChange={(event) => setStudentId(event.target.value)}
                />
              </div>
            </div>

            {/* The name is passed as HINTS. This is the one screen where the
                two-children-one-name case can arise, because it is the only
                one that collects a name -- so the signup's own follow-up stays
                inline here rather than being replaced by the thank-you card. */}
            {nameComplete ? (
              <PictureDaySignup
                schoolId={school.value}
                lang={lang}
                student={{ first_name: firstName, last_name: lastName, student_id: studentId }}
                onSignedUp={(data) => { if (!data.needsStudentId) setSignedUp(true); }}
              />
            ) : (
              <p className={styles.studentHint}>{t.missingName}</p>
            )}
          </>
        )}
      </div>

      <div className={styles.privacy}>
        <div className={styles.privacyTitle}>{t.privacyTitle}</div>
        <div className={styles.privacyBody}>{t.privacyBody}</div>
      </div>

      <Link className={styles.langToggle} to={{ pathname: location.pathname, search: lang === 'es' ? '' : '?lang=es' }} translate="no">
        {lang === 'es' ? 'English' : 'Español'}
      </Link>
    </div>
  );
};

export default PictureDayRegistration;
